/**
 * "3 conversations need a name" — the captured threads Reppr would not guess
 * at, and one tap to file each.
 *
 * A conversation lands here only when it is genuinely unclear: a number on
 * two records, or a group with two clients in it. The card shows what was
 * said and who it might have been; filing puts it on that record, with the
 * same identity the helper used, so the next delivery of that day updates it
 * rather than asking again.
 *
 * It renders nothing at all when the inbox is empty, which is most days.
 */

import { useEffect, useState } from 'react'
import { listCaptureInbox, fileCaptureInboxItem, dismissCaptureInboxItem, type InboxItem, type InboxCandidate } from '../api/captureInbox'
import Card, { CardBody } from './Card'
import Button from './Button'
import { showToast } from './Toast'

const WHY: Record<string, string> = {
  ambiguous_identifier: 'That number is on two records',
  multiple_records: 'Two people in Reppr were in this conversation',
  no_match: 'Nobody in it is in Reppr',
}

const ROLE: Record<string, string> = { parent: 'parent', guardian: 'guardian', advisor: 'advisor' }

function when(value: string): string {
  const at = new Date(String(value).replace(' ', 'T'))
  if (Number.isNaN(at.getTime())) return '—'
  return at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function who(c: InboxCandidate): string {
  const role = c.counterpart && ROLE[c.counterpart] ? ` (${c.label || ROLE[c.counterpart]})` : ''
  return `${c.name ?? 'Unnamed record'}${role}`
}

export default function CaptureInbox() {
  const [items, setItems] = useState<InboxItem[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    listCaptureInbox()
      .then(rows => { if (!cancelled) setItems(rows) })
      // Silent: an agency with no capture device has no inbox, and this sits
      // on the dashboard beside things that matter more.
      .catch(() => { if (!cancelled) setItems([]) })
    return () => { cancelled = true }
  }, [])

  if (!items || items.length === 0) return null

  const act = async (item: InboxItem, run: () => Promise<unknown>, done: string) => {
    setBusy(item.id)
    try {
      await run()
      setItems(prev => (prev ?? []).filter(i => i.id !== item.id))
      showToast(done, 'success')
    } catch (err: any) {
      showToast(err.message, 'error')
    } finally {
      setBusy(null)
    }
  }

  return (
    <Card>
      <CardBody>
        <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem' }}>
          {items.length} conversation{items.length === 1 ? '' : 's'} need{items.length === 1 ? 's' : ''} a name
        </h3>
        <p style={{ margin: '0 0 1rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
          Captured, but Reppr would not guess who they were with.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {items.map(item => (
            <div key={item.id} style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
              <div style={{ fontSize: '0.8125rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>
                {when(item.last_message_at)}
                {item.message_count !== null && ` · ${item.message_count} message${item.message_count === 1 ? '' : 's'}`}
                {item.reason && ` · ${WHY[item.reason] ?? item.reason}`}
              </div>
              <div style={{ marginBottom: '0.5rem' }}>{item.summary ?? 'No summary — the messages were brief.'}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {item.candidates.map(c => (
                  <Button
                    key={`${c.entity_type}:${c.entity_id}`}
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={busy === item.id}
                    onClick={() => act(item, () => fileCaptureInboxItem(item.id, { entity_type: c.entity_type, entity_id: c.entity_id }), `Filed on ${c.name ?? 'that record'}.`)}
                  >
                    File on {who(c)}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy === item.id}
                  onClick={() => act(item, () => dismissCaptureInboxItem(item.id), 'Put away.')}
                >
                  Not a client
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  )
}
